import { PLATFORM_BASE } from "./api";

export interface HuddleCapabilities {
  meshSignaling: boolean;
  stun: string[];
  realtimeSfu: boolean;
  buzzRelayAudioPath: string;
  note: string;
}

type SignalIn =
  | { type: "peers"; peers: Array<{ peerId: string; displayName: string; pubkey?: string }> }
  | { type: "peer-joined"; peer: { peerId: string; displayName: string; pubkey?: string } }
  | { type: "peer-left"; peerId: string }
  | { type: "offer"; from: string; to: string; sdp: RTCSessionDescriptionInit }
  | { type: "answer"; from: string; to: string; sdp: RTCSessionDescriptionInit }
  | { type: "ice"; from: string; to: string; candidate: RTCIceCandidateInit }
  | { type: "error"; message: string };

export async function fetchHuddleCapabilities(): Promise<HuddleCapabilities> {
  const res = await fetch(`${PLATFORM_BASE}/huddle/capabilities`);
  if (!res.ok) throw new Error(`huddle capabilities HTTP ${res.status}`);
  return (await res.json()) as HuddleCapabilities;
}

function wsUrl(roomId: string): string {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  // Prefer same-origin /platform path (Caddy → Worker)
  return `${proto}://${location.host}/platform/huddle/ws/${encodeURIComponent(roomId)}`;
}

/**
 * Mesh huddle over Cloudflare Worker Durable Object signaling + STUN.
 * Publishes Buzz huddle lifecycle events separately via BuzzRelayClient.
 */
export class MeshHuddle {
  readonly roomId: string;
  readonly peerId: string;
  private displayName: string;
  private pubkey?: string;
  private ws: WebSocket | null = null;
  private localStream: MediaStream | null = null;
  private pcs = new Map<string, RTCPeerConnection>();
  private remoteAudios = new Map<string, HTMLAudioElement>();
  private stun: string[] = ["stun:stun.cloudflare.com:3478"];
  onPeersChanged: ((peers: string[]) => void) | null = null;
  onStatus: ((text: string) => void) | null = null;

  constructor(roomId: string, displayName: string, pubkey?: string) {
    this.roomId = roomId;
    this.peerId = crypto.randomUUID();
    this.displayName = displayName;
    this.pubkey = pubkey;
  }

  async start() {
    const caps = await fetchHuddleCapabilities();
    this.stun = caps.stun?.length ? caps.stun : this.stun;
    this.onStatus?.(
      caps.realtimeSfu
        ? "Mesh + Realtime SFU available"
        : "Mesh huddle (Cloudflare STUN). Add CALLS_APP_* for SFU scale.",
    );

    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: false,
    });

    this.ws = new WebSocket(wsUrl(this.roomId));
    await new Promise<void>((resolve, reject) => {
      if (!this.ws) return reject(new Error("no ws"));
      this.ws.onopen = () => resolve();
      this.ws.onerror = () => reject(new Error("huddle signaling failed"));
    });

    this.ws.send(
      JSON.stringify({
        type: "join",
        peerId: this.peerId,
        displayName: this.displayName,
        pubkey: this.pubkey,
      }),
    );

    this.ws.onmessage = (event) => {
      void this.onSignal(JSON.parse(String(event.data)) as SignalIn);
    };
  }

  async stop() {
    try {
      this.ws?.send(JSON.stringify({ type: "leave", peerId: this.peerId }));
    } catch {
      // ignore
    }
    this.ws?.close();
    this.ws = null;
    for (const pc of this.pcs.values()) pc.close();
    this.pcs.clear();
    for (const audio of this.remoteAudios.values()) {
      audio.srcObject = null;
      audio.remove();
    }
    this.remoteAudios.clear();
    this.localStream?.getTracks().forEach((t) => t.stop());
    this.localStream = null;
    this.emitPeers();
  }

  private emitPeers() {
    this.onPeersChanged?.([...this.pcs.keys()]);
  }

  private createPc(remoteId: string): RTCPeerConnection {
    const existing = this.pcs.get(remoteId);
    if (existing) return existing;
    const pc = new RTCPeerConnection({
      iceServers: this.stun.map((url) => ({ urls: url })),
    });
    this.localStream?.getTracks().forEach((track) => {
      pc.addTrack(track, this.localStream!);
    });
    pc.onicecandidate = (ev) => {
      if (!ev.candidate || !this.ws) return;
      this.ws.send(
        JSON.stringify({
          type: "ice",
          from: this.peerId,
          to: remoteId,
          candidate: ev.candidate.toJSON(),
        }),
      );
    };
    pc.ontrack = (ev) => {
      let audio = this.remoteAudios.get(remoteId);
      if (!audio) {
        audio = document.createElement("audio");
        audio.autoplay = true;
        audio.setAttribute("playsinline", "true");
        document.body.appendChild(audio);
        this.remoteAudios.set(remoteId, audio);
      }
      audio.srcObject = ev.streams[0] ?? new MediaStream([ev.track]);
    };
    this.pcs.set(remoteId, pc);
    this.emitPeers();
    return pc;
  }

  private async onSignal(msg: SignalIn) {
    switch (msg.type) {
      case "peers":
        for (const peer of msg.peers) {
          await this.call(peer.peerId);
        }
        break;
      case "peer-joined":
        // Existing peers call the newcomer (lower id offers to avoid glare).
        if (this.peerId < msg.peer.peerId) {
          await this.call(msg.peer.peerId);
        }
        break;
      case "peer-left":
        this.pcs.get(msg.peerId)?.close();
        this.pcs.delete(msg.peerId);
        this.remoteAudios.get(msg.peerId)?.remove();
        this.remoteAudios.delete(msg.peerId);
        this.emitPeers();
        break;
      case "offer": {
        const pc = this.createPc(msg.from);
        await pc.setRemoteDescription(msg.sdp);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        this.ws?.send(
          JSON.stringify({
            type: "answer",
            from: this.peerId,
            to: msg.from,
            sdp: answer,
          }),
        );
        break;
      }
      case "answer": {
        const pc = this.pcs.get(msg.from);
        if (pc) await pc.setRemoteDescription(msg.sdp);
        break;
      }
      case "ice": {
        const pc = this.pcs.get(msg.from);
        if (pc && msg.candidate) {
          try {
            await pc.addIceCandidate(msg.candidate);
          } catch {
            // ignore late candidates
          }
        }
        break;
      }
      case "error":
        this.onStatus?.(msg.message);
        break;
      default: {
        const _exhaustive: never = msg;
        void _exhaustive;
      }
    }
  }

  private async call(remoteId: string) {
    const pc = this.createPc(remoteId);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    this.ws?.send(
      JSON.stringify({
        type: "offer",
        from: this.peerId,
        to: remoteId,
        sdp: offer,
      }),
    );
  }
}
